(() => {
  const setPlayButtonState = (hero, playing) => {
    const playBtn = hero.querySelector('[data-nano-hero-play]');
    if (!playBtn) return;

    const playIcon = playBtn.querySelector('.nano-hero__play-icon--play');
    const pauseIcon = playBtn.querySelector('.nano-hero__play-icon--pause');
    const playLabel = playBtn.dataset.labelPlay;
    const pauseLabel = playBtn.dataset.labelPause;

    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (playLabel && pauseLabel) {
      playBtn.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    }
    playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    hero.classList.toggle('is-playing', playing);
  };

  const playHeroVideo = (hero) => {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const hostedDesktop = hero.querySelector('.nano-hero__bg-video--desktop');
    const hostedMobile = hero.querySelector('.nano-hero__bg-video--mobile');
    const hosted = isMobile ? hostedMobile || hostedDesktop : hostedDesktop || hostedMobile;
    const iframe = hero.querySelector('[data-nano-hero-video-iframe]');

    if (hosted) {
      hosted.muted = true;
      hosted.loop = true;
      hosted.controls = false;
      const playPromise = hosted.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else if (iframe) {
      const provider = isMobile
        ? iframe.dataset.nanoVideoProviderMobile || iframe.dataset.nanoVideoProviderDesktop
        : iframe.dataset.nanoVideoProviderDesktop || iframe.dataset.nanoVideoProviderMobile;
      if (provider === 'youtube') {
        const id = isMobile
          ? iframe.dataset.nanoYoutubeIdMobile || iframe.dataset.nanoYoutubeIdDesktop
          : iframe.dataset.nanoYoutubeIdDesktop || iframe.dataset.nanoYoutubeIdMobile;
        if (!id) return;
        iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&iv_load_policy=3&disablekb=1&rel=0&modestbranding=1`;
      } else if (provider === 'vimeo') {
        const id = isMobile
          ? iframe.dataset.nanoVimeoIdMobile || iframe.dataset.nanoVimeoIdDesktop
          : iframe.dataset.nanoVimeoIdDesktop || iframe.dataset.nanoVimeoIdMobile;
        if (!id) return;
        iframe.src = `https://player.vimeo.com/video/${id}?background=1&autoplay=1&loop=1&muted=1&controls=0`;
      }
    }

    setPlayButtonState(hero, true);
  };

  const pauseHeroVideo = (hero) => {
    const hosted = hero.querySelectorAll('.nano-hero__bg-video');
    const iframe = hero.querySelector('[data-nano-hero-video-iframe]');

    if (hosted.length > 0) {
      hosted.forEach((video) => video.pause());
    }
    if (iframe) {
      iframe.removeAttribute('src');
    }

    setPlayButtonState(hero, false);
  };

  const toggleHeroVideo = (hero) => {
    if (hero.classList.contains('is-playing')) {
      pauseHeroVideo(hero);
    } else {
      playHeroVideo(hero);
    }
  };

  const syncNavMenuState = (nav) => {
    if (!nav) return;
    const hasOpenDropdown = nav.querySelector('[data-nano-nav-dropdown][open]');
    nav.classList.toggle('is-menu-open', Boolean(hasOpenDropdown));
  };

  const syncHeroContentOffset = (hero) => {
    const styles = window.getComputedStyle(hero);
    const xPercent = Number.parseFloat(styles.getPropertyValue('--nano-hero-content-x-percent')) || 0;
    const yPercent = Number.parseFloat(styles.getPropertyValue('--nano-hero-content-y-percent')) || 0;

    hero.style.setProperty('--nano-hero-content-x-offset', `${hero.offsetWidth * xPercent / 100}px`);
    hero.style.setProperty('--nano-hero-content-y-offset', `${hero.offsetHeight * yPercent / 100}px`);
  };

  const initNavDropdowns = (hero, nav) => {
    const dropdowns = Array.from(hero.querySelectorAll('[data-nano-nav-dropdown]'));
    if (dropdowns.length === 0) return;

    const desktopHoverQuery = window.matchMedia('(min-width: 1025px)');
    const closeTimers = new WeakMap();
    const isHoverDropdown = (dropdown) =>
      dropdown.classList.contains('nano-hero__dropdown') &&
      dropdown.dataset.nanoDropdownTrigger !== 'click' &&
      desktopHoverQuery.matches;

    const clearCloseTimer = (dropdown) => {
      const timer = closeTimers.get(dropdown);
      if (!timer) return;
      window.clearTimeout(timer);
      closeTimers.delete(dropdown);
    };

    const closeDropdown = (dropdown) => {
      clearCloseTimer(dropdown);
      dropdown.removeAttribute('open');
    };

    const openDropdown = (dropdown) => {
      clearCloseTimer(dropdown);
      dropdown.setAttribute('open', '');
    };

    const scheduleCloseDropdown = (dropdown) => {
      clearCloseTimer(dropdown);
      closeTimers.set(
        dropdown,
        window.setTimeout(() => {
          dropdown.removeAttribute('open');
          closeTimers.delete(dropdown);
          syncNavMenuState(nav);
        }, 300)
      );
    };

    dropdowns.forEach((dropdown) => {
      const summary = dropdown.querySelector('summary');

      dropdown.addEventListener('mouseenter', () => {
        if (!isHoverDropdown(dropdown)) return;
        openDropdown(dropdown);
        syncNavMenuState(nav);
      });

      dropdown.addEventListener('mouseleave', () => {
        if (!isHoverDropdown(dropdown)) return;
        scheduleCloseDropdown(dropdown);
      });

      summary?.addEventListener('click', (event) => {
        if (!isHoverDropdown(dropdown) || event.detail === 0) return;
        event.preventDefault();
      });

      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) {
          dropdowns.forEach((other) => {
            if (other !== dropdown) closeDropdown(other);
          });
        }
        syncNavMenuState(nav);
      });
    });

    document.addEventListener('click', (event) => {
      dropdowns.forEach((dropdown) => {
        if (!dropdown.contains(event.target)) {
          closeDropdown(dropdown);
        }
      });
      syncNavMenuState(nav);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      dropdowns.forEach(closeDropdown);
      syncNavMenuState(nav);
    });

    desktopHoverQuery.addEventListener('change', () => {
      dropdowns.forEach(closeDropdown);
      syncNavMenuState(nav);
    });
  };

  const initNavAnchorHighlight = (hero) => {
    const links = Array.from(hero.querySelectorAll('[data-nano-nav-anchor]'));
    if (links.length === 0) return;

    const sections = links
      .map((link) => {
        const id = link.getAttribute('href')?.replace('#', '');
        if (!id) return null;
        const section = document.getElementById(id);
        return section ? { link, section } : null;
      })
      .filter(Boolean);

    if (sections.length === 0) return;

    console.info('[nano-hero] anchor config map', {
      links: links.map((link) => ({
        label: link.dataset.nanoNavAnchorLabel || link.textContent?.trim() || '',
        href: link.getAttribute('href') || '',
        targetId: link.getAttribute('href')?.replace('#', '') || '',
        targetExists: Boolean(
          document.getElementById(link.getAttribute('href')?.replace('#', '') || '')
        ),
      })),
    });

    const setActive = (activeLink) => {
      links.forEach((link) => {
        link.closest('.nano-hero__dropdown-item')?.classList.toggle(
          'is-active',
          link === activeLink
        );
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          if (visible.length === 0) return;

          const match = sections.find(({ section }) => section === visible[0].target);
          if (match) setActive(match.link);
        },
        { rootMargin: '-40% 0px -45% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      sections.forEach(({ section }) => observer.observe(section));
    }
  };

  const getSamePageTarget = (href) => {
    if (!href) return null;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return null;
    }

    if (
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      !url.hash ||
      url.hash === '#'
    ) {
      return null;
    }

    return document.getElementById(decodeURIComponent(url.hash.slice(1)));
  };

  const initNavLinkClicks = (hero, nav, toggle, panel, isMobileNav) => {
    if (!nav) return;

    nav.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !nav.contains(link)) return;

      const href = link.getAttribute('href') || '';
      const target = getSamePageTarget(href);
      const label = link.textContent?.trim() || link.dataset.nanoNavAnchorLabel || '';

      console.info('[nano-hero] nav link click', {
        label,
        href,
        isMobile: isMobileNav(),
        targetId: target?.id || '',
        targetExists: Boolean(target),
        hasDropdown: Boolean(link.closest('[data-nano-nav-dropdown]')),
      });

      if (toggle && panel && isMobileNav()) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      }

      nav.querySelectorAll('[data-nano-nav-dropdown]').forEach((dropdown) => {
        dropdown.removeAttribute('open');
      });

      if (!target) return;

      event.preventDefault();
      const navHeight = nav.classList.contains('is-fixed') ? nav.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - navHeight;
      window.history.pushState(null, '', href);
      window.scrollTo({ top, behavior: 'smooth' });
    });
  };

  const initNanoNavDrag = (hero) => {
    const navDrag = hero.querySelector('[data-nano-nav-drag]');
    const viewport = hero.querySelector('[data-nano-nav-viewport]');
    const track = hero.querySelector('[data-nano-nav-track]');
    const prevBtn = hero.querySelector('[data-nano-nav-prev]');
    const nextBtn = hero.querySelector('[data-nano-nav-next]');

    if (!navDrag || !viewport || !track) return;

    let minX = 0;
    let currentX = 0;
    let isDragging = false;
    let hasDragged = false;
    let suppressClick = false;
    let startX = 0;
    let startTranslate = 0;
    const DRAG_THRESHOLD = 5;

    const clampX = (x) => Math.max(minX, Math.min(0, x));

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === 1) return event.deltaX * 16;
      if (event.deltaMode === 2) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const setX = (x, { animate = false } = {}) => {
      currentX = clampX(x);
      track.style.transition = animate ? 'transform 320ms ease' : 'none';
      track.style.transform = `translate3d(${currentX}px, 0, 0)`;
      updateOverflowState();
    };

    const updateOverflowState = () => {
      const hasOverflow = minX < 0;

      navDrag.classList.toggle('is-overflowing', hasOverflow);
      navDrag.classList.toggle('has-overflow-left', hasOverflow && currentX < -1);
      navDrag.classList.toggle('has-overflow-right', hasOverflow && currentX > minX + 1);
    };

    const moveTo = (x) => {
      setX(x, { animate: true });
    };

    const refresh = () => {
      const overflow = track.scrollWidth - viewport.clientWidth;
      minX = overflow > 1 ? -overflow : 0;

      setX(minX === 0 ? 0 : currentX);
    };

    const scheduleRefresh = () => {
      refresh();
      requestAnimationFrame(refresh);
      requestAnimationFrame(() => requestAnimationFrame(refresh));
      window.setTimeout(refresh, 40);
      window.setTimeout(refresh, 120);
    };

    prevBtn?.addEventListener('click', () => {
      moveTo(currentX + viewport.clientWidth * 0.65);
    });

    nextBtn?.addEventListener('click', () => {
      moveTo(currentX - viewport.clientWidth * 0.65);
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (minX === 0) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setX(currentX - deltaX);
      },
      { passive: false }
    );

    viewport.addEventListener('pointerdown', (event) => {
      if (minX === 0 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      isDragging = true;
      hasDragged = false;
      startX = event.clientX;
      startTranslate = currentX;
      track.style.transition = 'none';
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      const delta = event.clientX - startX;
      if (!hasDragged) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        hasDragged = true;
      }
      event.preventDefault();
      setX(startTranslate + delta);
    });

    const stopDrag = (event) => {
      if (!isDragging) return;
      const didDrag = hasDragged;
      isDragging = false;
      viewport.releasePointerCapture?.(event.pointerId);
      hasDragged = false;
      if (didDrag) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 180);
      }
      setX(currentX);
    };

    track.addEventListener('dragstart', (event) => event.preventDefault());

    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    viewport.addEventListener('pointerup', stopDrag);
    viewport.addEventListener('pointercancel', stopDrag);

    track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', refresh, { once: true });
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(scheduleRefresh, 60);
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleRefresh);
      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
    }

    scheduleRefresh();
    window.addEventListener('load', scheduleRefresh, { once: true });
    document.fonts?.ready?.then(scheduleRefresh).catch(() => {});
  };

  document.querySelectorAll('[data-nano-hero]').forEach((hero) => {
    const nav = hero.querySelector('[data-nano-sticky-nav]');
    const sentinel = hero.querySelector('[data-nano-nav-sentinel]');
    const slot = hero.querySelector('[data-nano-nav-slot]');
    const toggle = hero.querySelector('[data-nano-menu-toggle]');
    const panel = hero.querySelector('[data-nano-mobile-menu]');
    const playBtn = hero.querySelector('[data-nano-hero-play]');
    const mobileNavQuery = window.matchMedia('(max-width: 1024px)');
    const isMobileNav = () => mobileNavQuery.matches;
    const navTriggerSelector = hero.dataset.nanoNavTriggerSelector?.trim();

    const getNavTriggerThreshold = () => {
      if (!navTriggerSelector) return window.innerHeight;

      try {
        const trigger = document.querySelector(navTriggerSelector);
        if (trigger) {
          return trigger.getBoundingClientRect().top + window.scrollY;
        }
      } catch (error) {
        // Invalid selectors fall back to the existing viewport-height trigger.
      }

      return window.innerHeight;
    };

    const mountNavToBody = () => {
      if (!nav || !slot || nav.parentElement === document.body) return;
      document.body.appendChild(nav);
    };

    const restoreNavToSlot = () => {
      if (!nav || !slot || nav.parentElement === slot) return;
      slot.appendChild(nav);
    };

    const resetNav = () => {
      if (!nav || !slot) return;
      nav.classList.remove('is-stuck', 'is-fixed', 'is-hiding', 'is-menu-open');
      restoreNavToSlot();
      slot.style.height = '';
      hero.querySelectorAll('[data-nano-nav-dropdown]').forEach((dropdown) => {
        dropdown.removeAttribute('open');
      });
      if (toggle && panel) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      }
    };

    let navInteractionsReady = false;

    const ensureNavInteractions = () => {
      if (navInteractionsReady) return;
      initNavDropdowns(hero, nav);
      initNavLinkClicks(hero, nav, toggle, panel, isMobileNav);
      initNavAnchorHighlight(hero);
      navInteractionsReady = true;
    };

    let hideNavTimer = null;
    let hideNavListener = null;

    const clearHideNavTransition = () => {
      if (hideNavTimer) {
        window.clearTimeout(hideNavTimer);
        hideNavTimer = null;
      }
      if (hideNavListener) {
        nav.removeEventListener('transitionend', hideNavListener);
        hideNavListener = null;
      }
    };

    const finalizeHideNav = () => {
      clearHideNavTransition();
      nav.classList.remove('is-fixed', 'is-hiding');
      restoreNavToSlot();
      slot.style.height = '';
    };

    const setStuck = (stuck) => {
      if (!nav || !slot) return;

      if (stuck) {
        clearHideNavTransition();
        nav.classList.remove('is-hiding');

        if (!nav.classList.contains('is-fixed')) {
          mountNavToBody();
          slot.style.height = `${nav.offsetHeight}px`;
          nav.classList.add('is-fixed');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (stuck) {
                nav.classList.add('is-stuck');
              }
            });
          });
          return;
        }

        nav.classList.add('is-stuck');
        return;
      }

      if (!nav.classList.contains('is-fixed')) return;

      clearHideNavTransition();
      nav.classList.add('is-hiding');
      nav.classList.remove('is-stuck');

      hideNavListener = (event) => {
        if (event.target !== nav || event.propertyName !== 'transform') return;
        finalizeHideNav();
      };

      nav.addEventListener('transitionend', hideNavListener);
      hideNavTimer = window.setTimeout(finalizeHideNav, 380);
    };

    const syncByScreen = () => {
      syncHeroContentOffset(hero);
      ensureNavInteractions();
      const threshold = getNavTriggerThreshold();
      setStuck(window.scrollY >= threshold);
    };

    initNanoNavDrag(hero);

    syncByScreen();
    window.addEventListener('scroll', syncByScreen, { passive: true });
    window.addEventListener('resize', syncByScreen);
    mobileNavQuery.addEventListener('change', syncByScreen);

    if (hero.dataset.autoplayVideo === 'true') {
      playHeroVideo(hero);
    }

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        toggleHeroVideo(hero);
      });
    }

    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      if (!isMobileNav()) return;
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });

    panel.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !isMobileNav()) return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    });
  });
})();
